import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getOktaToken,
  selectAuthState,
  setAuthData,
} from "@/store/features/authSlice"
import {
  createCart,
  getCart,
  selectCartState,
  setCardStatus,
} from "@/store/features/cartSlice"
import usePrevious from "@/hooks/usePrevious"
import { getCurrency, isGuestTokenValid, revalidateTime } from "@/utils/helper"
import { setCartLandingPagePath } from "@/utils/cart"
import useIsSsr from "@/hooks/useIsSsr"
import { getConfig } from "@/constants/config"

const usePageAuth = () => {
  const isSsr = useIsSsr()
  const dispatch = useDispatch()
  const { access_token: accessToken } = useSelector(selectAuthState)
  const { cart: activeCart, isCartLoading } = useSelector(selectCartState)
  const prevToken = usePrevious({ accessToken })
  const isFetchingToken = useRef(false)

  // Initialize Redux state with stored guest token from localStorage on app load
  useEffect(() => {
    if (isSsr || accessToken) return

    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      const storedToken = localStorage.getItem("okta-anonymous-access-token")
      if (storedToken && isGuestTokenValid()) {
        dispatch(
          setAuthData({
            accessToken: storedToken,
            revalidateTime: revalidateTime(),
          })
        )
      }
    }
  }, [isSsr])

  // just for the dev testing purpose, if works fine then can only be proceed otherwise
  // one more pr will be raised to revert the change
  useEffect(() => {
    let timeoutId
    if (!isSsr) {
      timeoutId = setTimeout(() => {
        if (!accessToken || !isGuestTokenValid()) {
          if (!isFetchingToken.current) {
            isFetchingToken.current = true
            dispatch(getOktaToken("guest")).finally(() => {
              isFetchingToken.current = false
            })
          }
        }
      }, 5000)
    }
    // eslint-disable-next-line no-extra-semi
    return () => {
      clearTimeout(timeoutId)
    }
  }, [accessToken, isSsr])

  const callInitAuth = async () => {
    const config = await getConfig()
    const disableCommerce = config?.general?.disableCommerce || false

    if (disableCommerce) return

    const hasNoActiveCart = () => {
      return (
        isGuestTokenValid() &&
        accessToken &&
        !activeCart?.id &&
        !activeCart?.cartExists
      )
    }
    if (
      hasNoActiveCart() ||
      (hasNoActiveCart() && prevToken?.accessToken !== accessToken)
    ) {
      if (!isCartLoading) {
        dispatch(setCardStatus(true))
        dispatch(getCart())
          .unwrap()
          .then(res => {
            if (
              isGuestTokenValid() &&
              accessToken &&
              res?.cartExists &&
              res?.cartExists === "false" &&
              res?.error === "No active cart exists."
            ) {
              getCurrency().then(currency => {
                const cartPayload = {
                  currency: currency,
                  taxMode: "ExternalAmount",
                }
                dispatch(createCart(cartPayload))
                  .unwrap()
                  .then(() => {
                    dispatch(setCardStatus(false))
                  })
              })
            } else {
              dispatch(setCardStatus(false))
            }
          })
      }
    }
    setCartLandingPagePath()
  }

  useEffect(() => {
    if (isSsr) return
    callInitAuth()
  }, [accessToken, isSsr])
}

export default usePageAuth
